import {createContext} from "react";
import React, {ReactElement, useState, useEffect} from "react";
import {IUser, IUserRegisterRequest} from "@plumeuk/shapeshift-types";
import {API} from ".";
import {DevLog} from "./commonFunctions/devLog";
import {isWindow, isServer} from "./commonFunctions/isWindow";
import hitApi from "./hitApi";
import {IAuthContext, IAuthContextConfig, ILoginResponse, ILoginRequest, IChangePasswordRequest, IResetPasswordRequest, IForgetPasswordRequest} from "./types/IAuth";


export const AuthContext = createContext<IAuthContext>({
	login: () => Promise.reject(new Error("Login not initialized, check library dependency. Likely issue with multiple context providers.")),
	logout: () => Promise.reject(new Error("Logout not initialized, check library dependency. Likely issue with multiple context providers.")),
	jwt: undefined,
	user: undefined,
	authenticated: false,
	config: undefined,
	changePassword: () => Promise.reject(new Error("Change Password not initialized, check library dependency. Likely issue with multiple context providers.")),
	registerUser: () => Promise.reject(new Error("Register User not initialized, check library dependency. Likely issue with multiple context providers.")),
	forgottenPassword: (_email: string) => Promise.reject(new Error("Forgotten Password not initialized, check library dependency. Likely issue with multiple context providers.")),
	resetPassword: (_code: string, _password: string, _passwordConfirmation: string) => Promise.reject(new Error("Reset Password not initialized, check library dependency. Likely issue with multiple context providers.")),
	setUser: () => { throw new Error("Set User not initialized, check library dependency. Likely issue with multiple context providers.") }
});

//simple but will likely do more in future
const validateAuth = (jwt?: string, user?: unknown): boolean => (!!jwt && !!user);

const getUser = (): IUser | undefined => {
	if(isServer())
		return undefined;

	const userJson = localStorage.getItem("user");
	return userJson ? (JSON.parse(userJson)) : undefined;
}

export interface IProps {
	children: ReactElement,
	contextConfig: IAuthContextConfig,
	jwt?: string,
	user?: IUser,
	actionOverrideValidateAuth?: (jwt?: string, user?: unknown) => boolean,
	actionOverrideLogout?: () => void
}

export const AuthProvider = (props: IProps): JSX.Element => {
	const [jwt, setJwt] = useState<string | undefined>(props.jwt ?? (isWindow() ? (localStorage.getItem("jwt") ?? undefined) : undefined));
	const [user, setUser] = useState<IUser | undefined>(props?.user ?? getUser());
	const [config, setConfig] = useState<IAuthContextConfig>(props.contextConfig);
	const [authenticated, setAuthenticated] = useState<boolean>(props?.actionOverrideValidateAuth?.(jwt, user) ?? validateAuth(jwt, user));

	useEffect(() => setConfig(props.contextConfig), [props.contextConfig])

	useEffect(() => setAuthenticated(props?.actionOverrideValidateAuth?.(jwt, user) ?? validateAuth(jwt, user)), [jwt, user])

	const setJwtInLocalStorage = (response: ILoginResponse): void => {
		if(isServer())
			return;
		if(response?.jwt){
			const {jwt, user} = response;

			setJwt(jwt);
			setUser(user);

			localStorage?.setItem?.("jwt", jwt);
			localStorage?.setItem?.("user", JSON.stringify(user));
		}
	}

	const login = async (username: string, password: string): Promise<ILoginResponse> => {
		if(isServer())
			throw new Error("Cannot call login on server");

		DevLog("Logging in");
		return hitApi<ILoginResponse, ILoginRequest>({
			url: API.login,
			data: {identifier: username, password},
			method: "POST"
		}, config)
			.then(response => {
				setJwtInLocalStorage(response);
				return response;
			});
	}

	const logout = (): void => props?.actionOverrideLogout ? props?.actionOverrideLogout?.() : (() => {
		if(isServer())
			throw new Error("Cannot call logout on server");


		DevLog("Logging out", props, ">>")
		localStorage?.removeItem?.("jwt");
		localStorage?.removeItem?.("user");
		setJwt(undefined);
		setUser(undefined);
	})();

	const changePassword = async (currentPassword: string, password: string, passwordConfirmation: string): Promise<ILoginResponse> => {
		DevLog("Changing Password");
		return hitApi<ILoginResponse, IChangePasswordRequest>({
			url: API.changePassword,
			data: {
				currentPassword,
				password,
				passwordConfirmation
			},
			method: "POST",
			config: {
				headers: {
					Authorization: `Bearer ${jwt}`
				}
			}
		}, config)
			.then(response => {
				setJwtInLocalStorage(response);
				return response;
			});
	}

	const resetPassword = async (code: string, password: string, passwordConfirmation: string): Promise<void> => {
		DevLog("Reset Password");
		return hitApi<void, IResetPasswordRequest>({
			url: API.resetPassword,
			data: {
				code,
				password,
				passwordConfirmation
			},
			method: "POST"
		}, config)
	}

	const forgottenPassword = async (email: string): Promise<void> => {
		DevLog("Forgotten Password");
		return hitApi<void, IForgetPasswordRequest>({
			url: API.forgottenPassword,
			data: {
				email
			},
			method: "POST"
		}, config)
	}

	const registerUser = async (request: IUserRegisterRequest, additionalPayload?: Object): Promise<ILoginResponse> => {
		DevLog("Registering User");
		return hitApi<ILoginResponse, IUserRegisterRequest>({
			url: API.registerUser,
			data: additionalPayload ? {...request, ...additionalPayload} : request,
			method: "POST"
		}, config, jwt)
			.then(response => {
				setJwtInLocalStorage(response);
				return response;
			});
	}

	useEffect(() => {
		DevLog("AUTH", authenticated);
	}, [authenticated])

	useEffect(() => {
		if(props.jwt)
			setJwt(props.jwt)
	}, [props.jwt])

	useEffect(() => {
		if(props.user)
			setUser(props.user)
	}, [props.user])

	useEffect(() => {
		if(isServer()) return;
		if(jwt)
			localStorage?.setItem?.("jwt", jwt);
		else
			localStorage?.removeItem?.("jwt")
	}, [jwt])

	useEffect(() => {
		if(isServer()) return;
		if(user)
			localStorage?.setItem?.("user", JSON.stringify(user))
		else
			localStorage?.removeItem?.("user")
	}, [user])

	return (
		<AuthContext.Provider value={{jwt, user, authenticated, login, logout, config, changePassword, registerUser, setUser, resetPassword, forgottenPassword}}>
			{props.children}
		</AuthContext.Provider>
	);
}

export default AuthProvider;